import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import {
  FILE_PREVIEW_TEXT_MAX,
  base64Bytes,
  filePreviewKind,
  filePreviewMime,
  type FilePreviewKind,
} from '../lib/filePreview.js';
import { Modal } from './Modal.js';

interface Loaded {
  kind: FilePreviewKind;
  /** the copy typed for the viewer (image / PDF) */
  url?: string;
  text?: string;
  /** a plain binary copy for Download */
  download: string;
}

/**
 * ehr#584 (Chase 10-21): a chart file in a preview window, as the client sees their files in the
 * portal. It loads through the signed-in /files/:id/content route (no new links) into blob copies
 * that live only while the window is open.
 */
export function FilePreviewModal({
  file,
  onClose,
}: {
  file: { id: string; name: string; mime?: string | null };
  onClose: () => void;
}) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    const urls: string[] = [];
    const made = (bytes: Uint8Array<ArrayBuffer>, type: string) => {
      const u = URL.createObjectURL(new Blob([bytes], { type }));
      urls.push(u);
      return u;
    };
    setLoaded(null);
    setErr('');
    api
      .get<{ name: string; mime: string; dataBase64: string }>(`/files/${file.id}/content`)
      .then((d) => {
        if (!live) return;
        const name = d.name || file.name;
        const mime = d.mime || file.mime || '';
        const kind = filePreviewKind(name, mime);
        const bytes = base64Bytes(d.dataBase64);
        const out: Loaded = { kind, download: made(bytes, 'application/octet-stream') };
        if (kind === 'image' || kind === 'pdf') out.url = made(bytes, filePreviewMime(name, mime));
        if (kind === 'text') {
          const tx = new TextDecoder().decode(bytes);
          out.text =
            tx.length > FILE_PREVIEW_TEXT_MAX ? `${tx.slice(0, FILE_PREVIEW_TEXT_MAX)}\n…` : tx;
        }
        setLoaded(out);
      })
      .catch((e: unknown) => {
        if (!live) return;
        setErr(
          e instanceof ApiError && e.status === 403
            ? 'Your role cannot open contact files.'
            : e instanceof ApiError && (e.status === 404 || e.status === 410)
              ? 'This file was removed, so it can’t be opened.'
              : 'Could not open this file. Try again.',
        );
      });
    return () => {
      live = false;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [file.id]);

  return (
    <Modal
      title={file.name}
      onClose={onClose}
      className="fu-preview-modal file-preview-modal"
      actions={
        <>
          {loaded && (
            <a className="btn" href={loaded.download} download={file.name}>
              Download
            </a>
          )}
          <button type="button" className="btn primary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {err ? (
        <p className="muted">{err}</p>
      ) : !loaded ? (
        <p className="muted">Opening…</p>
      ) : loaded.kind === 'image' ? (
        <img className="fu-preview-img" src={loaded.url} alt={file.name} />
      ) : loaded.kind === 'pdf' ? (
        <iframe className="fu-preview-pdf" src={loaded.url} title={file.name} />
      ) : loaded.kind === 'text' ? (
        <pre className="file-preview-text">{loaded.text}</pre>
      ) : (
        <p className="muted file-preview-none">
          There&apos;s no preview for this kind of file. Download it to open it.
        </p>
      )}
    </Modal>
  );
}
